import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { COMPANY } from "@/data/site";

const EASE = [0.16, 1, 0.3, 1];

const FAQS = [
  {
    q: "How much protein is in each bar?",
    a: "Every DNA bar carries 10g of clean protein from a blend of whey and pea protein — a complete amino profile without the bloat that whey-only bars can leave behind.",
  },
  {
    q: "Do the bars contain added sugar?",
    a: "No. Our bars are sweetened with date paste, so the only sugars inside come naturally from whole-food ingredients.",
  },
  {
    q: "What is the difference between the Collagen and Creatine bars?",
    a: "The Collagen Bar (Cranberry Bliss) carries the Triple Thrive Formula — collagen, L-glutamine and ashwagandha — for skin, hair and recovery. The Creatine Bar (Choco Almond) carries the Triple Threat Formula — creatine, ashwagandha and L-glutamine — for strength, power and daily performance.",
  },
  {
    q: "How long does delivery take?",
    a: "Orders are usually dispatched within 1–2 working days and arrive within 3–7 working days across India. Shipping is free on all orders, and you'll get a tracking link by email once your order ships.",
  },
  {
    q: "Can I return or get a refund on my order?",
    a: "DNA bars are a food product, so we can't accept returns or offer refunds once an order is delivered. You can cancel any time before your order ships for a full refund.",
  },
  {
    q: "How does the monthly subscription work?",
    a: "Build your own box, split it between Collagen and Creatine, and it arrives every month at a lower price. You can pause or cancel any time before your next renewal.",
  },
  {
    q: "How do loyalty points work?",
    a: "You earn points on every paid order and can redeem them at checkout on your next one. Track your balance any time from My Account.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(null);

  return (
    <section id="faq" className="py-32 md:py-48 border-b border-[#EBE8E3]" data-testid="faq-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: EASE }}
          className="md:col-span-4"
        >
          <p className="text-sm md:text-base tracking-[0.3em] uppercase text-[#C07C5F] mb-6">FAQ</p>
          <h2 className="font-serif-lux font-medium text-5xl md:text-7xl tracking-tighter leading-[0.95]">
            Questions,
            <span className="italic font-light text-[#5C5C5C]"> answered.</span>
          </h2>
          <p className="mt-8 text-sm md:text-base font-light text-[#5C5C5C] leading-relaxed max-w-sm">
            Can't find what you're looking for? Write to us at{" "}
            <a href={`mailto:${COMPANY.email}`} className="text-[#C07C5F] underline underline-offset-4">
              {COMPANY.email}
            </a>
            .
          </p>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
          className="md:col-span-8 border-t border-[#EBE8E3]"
        >
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <li key={item.q} className="border-b border-[#EBE8E3]">
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left font-serif-lux text-xl md:text-2xl font-medium tracking-tight hover:text-[#C07C5F] transition-colors duration-500"
                >
                  {item.q}
                  <Plus size={18} className={`shrink-0 text-[#C07C5F] transition-transform duration-500 ${isOpen ? "rotate-45" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <p className="pb-6 pr-10 text-sm md:text-base font-light text-[#5C5C5C] leading-relaxed" data-testid={`faq-answer-${i}`}>
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            );
          })}
        </motion.ul>
      </div>
    </section>
  );
};

export default FAQ;
